import AppleIcon from '@mui/icons-material/Apple';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Image from 'next/image';
import React, { useEffect, useRef, useMemo } from 'react';
import { BrowserType, detectBrowser } from '@/utils/detectBrowser.utils';
import { Images } from '../assets';
import { ContentData, SocialAuthOption } from '../interface';
import { useAccessToken } from '../providers/AccessTokenProvider';
import ClaimContainer from './ClaimContainer';
import InfoContainer from './InfoContainer';
import SecurityContainer from './SecurityContainer';
import TextInput from './TextInput';

export interface ConnectWalletContainerProps {
  content?: ContentData;
  email: string;
  error?: string;
  logoUrl?: string;
  onChangeEmail: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClickContinueWithEmail: () => void;
  onClickExistingWallet: () => void;
  onClickContinueWithSocial: (type: SocialAuthOption) => void;
}

const ConnectWalletContainer = ({
  content,
  email,
  error,
  onChangeEmail,
  onClickContinueWithEmail,
  onClickExistingWallet,
  onClickContinueWithSocial
}: ConnectWalletContainerProps) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const { accessToken } = useAccessToken();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setAttribute('autoComplete', 'off');
    }
  }, []);

  const isBrowserSupport = useMemo(() => {
    const browserType = detectBrowser();

    return browserType === BrowserType.instagram;
  }, []);

  return (
    <Box>
      <ClaimContainer
        description={
          !accessToken
            ? "We apologize for the confusion and please don't take that question personally. Please check the box to verify that you are not a bot."
            : ''
        }
        title={!accessToken ? 'Are you a person?' : content?.title || 'Connect to Mojito'}
      />
      {accessToken ? (
        <>
          <TextInput
            inputRef={inputRef}
            placeholder={email ? '' : 'name@email.com'}
            InputPropsStyle={{
              height: '40px',
              color: theme?.palette?.common?.black,
              fontFamily: theme?.MojitoClaim?.font?.secondary
            }}
            sx={{
              width: '100%',
              marginBottom: '15px'
            }}
            value={email}
            onChange={onChangeEmail}
            error={error}
          />
          <Button
            sx={{
              width: '100%',
              marginBottom: '20px'
            }}
            onClick={onClickContinueWithEmail}
          >
            Continue
          </Button>
          <Divider
            sx={{
              marginBottom: '20px',
              fontFamily: theme?.MojitoClaim?.font?.secondary
            }}
            orientation="horizontal"
            flexItem
          >
            or
          </Divider>
          <Stack marginBottom="20px" direction="row" spacing={2}>
            <Button
              disabled={isBrowserSupport}
              onClick={() => onClickContinueWithSocial('google')}
              startIcon={<Image src={Images.GOOGLE} width={16} height={16} alt="google" />}
              sx={{
                width: '100%',
                color: theme?.palette?.primary?.main,
                border: `1px solid ${theme?.palette?.primary?.main}`,
                background: theme?.palette?.background?.paper
              }}
            >
              Sign in with Google
            </Button>
            <Button
              disabled={isBrowserSupport}
              onClick={() => onClickContinueWithSocial('apple')}
              startIcon={
                <AppleIcon
                  sx={{
                    fontSize: '16px',
                    color: theme.palette.background.default
                  }}
                />
              }
              sx={{
                width: '100%',
                color: theme?.palette?.primary?.main,
                border: `1px solid ${theme?.palette?.primary?.main}`,
                background: theme?.palette?.background?.paper,
                marginBottom: '20px'
              }}
            >
              Sign in with Apple
            </Button>
          </Stack>
          {isBrowserSupport && (
            <InfoContainer description="Sign in with Google or Sign in with Apple are not available in this browser. Please try using a different browser." />
          )}
          <Button
            onClick={onClickExistingWallet}
            sx={{
              width: '100%',
              color: theme?.palette?.primary?.main,
              border: `1px solid ${theme?.palette?.primary?.main}`,
              background: theme?.palette?.background?.paper
            }}
          >
            Connect another wallet
          </Button>
        </>
      ) : (
        <SecurityContainer />
      )}
    </Box>
  );
};

export default ConnectWalletContainer;
